"use client";

import Image from "next/image";
import { CircleCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import { CcFortnoxVideo } from "./CcFortnoxVideo";
import type { FortnoxSubpageCopy } from "./fortnox-page-copy";

type CcFortnoxHeroStageProps = {
  video: FortnoxSubpageCopy["hero"]["video"];
  feed: FortnoxSubpageCopy["hero"]["feed"];
  fortnoxLogo: string;
};

const FEED_MS = 2600;

/**
 * Cinematic video under the hero copy (cartage.ai): the frame opens up to
 * full width as it scrolls in, and a live sync feed on top shows sales,
 * payments and customers landing in Fortnox one after another.
 */
export function CcFortnoxHeroStage({ video, feed, fortnoxLogo }: CcFortnoxHeroStageProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const reduced = useCcPrefersReducedMotion();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || reduced) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = root.getBoundingClientRect();
      const progress = (window.innerHeight - rect.top) / (window.innerHeight * 0.75);
      root.style.setProperty("--cc-fx-p", Math.min(1, Math.max(0, progress)).toFixed(3));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [reduced]);

  useEffect(() => {
    if (reduced) return;
    const timer = window.setInterval(() => setTick((value) => value + 1), FEED_MS);
    return () => window.clearInterval(timer);
  }, [reduced]);

  // The three newest events, newest on top.
  const count = feed.items.length;
  const visible = [0, 1, 2].map((offset) => {
    const index = (((tick - offset) % count) + count) % count;
    return { ...feed.items[index], key: `${tick - offset}` };
  });

  return (
    <div ref={rootRef} className="cc-fx-stage" data-reduced={reduced ? "true" : undefined}>
      <div className="cc-fx-stage__frame">
        <CcFortnoxVideo
          src={video.src}
          srcSmall={video.srcSmall}
          poster={video.poster}
          className="cc-fx-stage__video"
          sizes="(min-width: 1472px) 1472px, 100vw"
          priority
        />
        <span className="cc-fx-stage__shade" aria-hidden="true" />
        <div className="cc-fx-feed" aria-hidden="true">
          <span className="cc-fx-feed__head">
            <span className="cc-fx-feed__brand">
              <Image src={fortnoxLogo} alt="" width={16} height={16} unoptimized loading="eager" />
            </span>
            {feed.title}
            <b>
              <i />
              {feed.status}
            </b>
          </span>
          <ul>
            {visible.map((item, index) => (
              <li key={item.key} data-fresh={index === 0 ? "true" : undefined}>
                <span className="cc-fx-feed__logo" data-wide={"wide" in item && item.wide ? "true" : undefined}>
                  <Image src={item.logo} alt="" width={"wide" in item && item.wide ? 26 : 16} height={16} unoptimized loading="eager" />
                </span>
                <span className="cc-fx-feed__text">
                  {item.text}
                  <small>
                    <CircleCheck size={11} strokeWidth={2.6} />
                    {item.result}
                  </small>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
